import {
  alpha,
  Box,
  ButtonBase,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Theme,
} from "@mui/material";
import { useCustomMutation } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { useMarketplaceStore } from "../../store/useMarketplaceStore";
import { useCurrency, CurrencyDisplay } from "../../hooks/useCurrency";

type CurrencyToggleProps = {
  /**
   * "icon": compact icon button showing the active symbol (default).
   * "row": full-width labeled row with a USD/local segmented control,
   * used in the sidebar account section.
   */
  variant?: "icon" | "row";
};

export const CurrencyToggle = ({ variant = "icon" }: CurrencyToggleProps) => {
  const selectedCountry = useMarketplaceStore((s) => s.selectedCountry);
  const { isUSD, isLoading, settings } = useCurrency();
  const { mutate, isLoading: isSaving } = useCustomMutation();
  const queryClient = useQueryClient();

  if (!selectedCountry) return null;

  const localSymbol = selectedCountry.currency;
  if (!localSymbol || localSymbol === "$") return null;

  if (isLoading || !settings) return null;

  const nextValue: CurrencyDisplay = isUSD ? "LOCAL_CURRENCY" : "USD";

  const handleClick = () => {
    if (isSaving) return;

    mutate(
      {
        url: `store/${selectedCountry.id}/settings`,
        method: "put",
        values: {
          ...settings,
          currencyDisplay: nextValue,
          // Backend rejects a currencyRate alongside AUTOMATIC — mirrors
          // the same rule GeneralSettings.tsx applies on submit.
          currencyRate:
            settings.currencyRateType === "AUTOMATIC"
              ? null
              : settings.currencyRate,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries();
        },
      },
    );
  };

  let tooltipTitle: string;
  if (isSaving) {
    tooltipTitle = "Kaydediliyor...";
  } else if (isUSD) {
    tooltipTitle = `Yerel para birimine geç (${localSymbol})`;
  } else {
    tooltipTitle = "USD'ye geç ($)";
  }

  if (variant === "row") {
    const segmentSx = (active: boolean) => ({
      px: 1,
      py: 0.375,
      fontSize: "0.7rem",
      fontWeight: 700,
      lineHeight: 1.2,
      color: active ? "primary.main" : "text.secondary",
      backgroundColor: active
        ? (theme: Theme) => alpha(theme.palette.primary.main, 0.12)
        : "transparent",
      transition: "background-color 0.15s ease, color 0.15s ease",
    });

    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1.25,
          py: 0.625,
          borderRadius: "8px",
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "customBackground.box",
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Para birimi
        </Typography>
        <Box
          sx={{
            display: "flex",
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            overflow: "hidden",
          }}
        >
          <ButtonBase
            disabled={isSaving || isUSD}
            onClick={handleClick}
            sx={segmentSx(isUSD)}
          >
            USD
          </ButtonBase>
          <ButtonBase
            disabled={isSaving || !isUSD}
            onClick={handleClick}
            sx={segmentSx(!isUSD)}
          >
            {localSymbol}
          </ButtonBase>
        </Box>
      </Stack>
    );
  }

  return (
    <Tooltip title={tooltipTitle}>
      <span>
        <IconButton
          color="inherit"
          onClick={handleClick}
          disabled={isSaving}
          aria-label="Para birimi değiştir"
          sx={{ width: 34, height: 34 }}
        >
          <Typography fontSize="1rem" fontWeight={700} lineHeight={1}>
            {isUSD ? "$" : localSymbol}
          </Typography>
        </IconButton>
      </span>
    </Tooltip>
  );
};
